import { filterMenu } from "@client/lib/menu";
import { pick } from "@client/lib/object";
import {
  combinedStyleSelector,
  createShallowEqualSelector,
  getLayoutColorSelector,
  getLayoutSettingValueSelector,
  getSettingValueSelector,
  menuItemsSelector,
  screenSelector,
  TargetSelector
} from "@client/selectors";
import { layoutSettingsSelector } from "@client/selectors/layout";
import { type GenericMap, type MenuItem } from "@client/types";
import { dotsMenuItemsSelector, onePageTypeSelector } from "@client/widgets/DotsMenu/selectors";
import { isMenuOverlay, menuEffectSelector } from "@client/widgets/Header/selectors";
import { createSelector } from "reselect";
import { getMegaOptions } from "./lib";

const finalItemStyleSelector = combinedStyleSelector(
  TargetSelector.Layout,
  ["border", "margin", "padding", "boxShadow"],
  "header.menu.item"
);

export const itemStyleSelector = createSelector(
  [menuEffectSelector, finalItemStyleSelector],
  (menuEffect, style): React.CSSProperties | undefined => (isMenuOverlay(menuEffect) ? undefined : style)
);

const itemTextHoverColorSelector = getLayoutColorSelector("hoverColor", "header.menu.item.text");

const itemBorderHoverColorSelector = getLayoutColorSelector("hoverColor", "header.menu.item.border");
const itemBackgroundTypeSelector = getSettingValueSelector(
  TargetSelector.Layout,
  "type",
  "header.menu.item.background"
);
const itemBackgroundHoverColorSelector = getLayoutColorSelector("hoverColor", "header.menu.item.background");

export const activeItemStyleSelector = createShallowEqualSelector(
  [itemTextHoverColorSelector],
  (color): React.CSSProperties => ({
    color
  })
);

export const itemHoverStyleSelector = createShallowEqualSelector(
  [
    itemTextHoverColorSelector,
    itemBorderHoverColorSelector,
    itemBackgroundTypeSelector,
    itemBackgroundHoverColorSelector
  ],
  (color, borderColor, backgroundType, backgroundColor): React.CSSProperties => ({
    backgroundColor: backgroundType === "color" ? backgroundColor ?? "var(--color)" : undefined,
    borderColor: borderColor ?? "var(--color)",
    color: color ?? "var(--color)"
  })
);

// 1. Add mega menu information to the menu
const megaMenuSettingsSelector = createSelector([layoutSettingsSelector], (settings) =>
  settings
    ? <GenericMap<any>>(
        pick(settings, [
          /megaMenuEnable\d+/,
          /mmTabName\d+/,
          /megaMenuColumns\d+/,
          /megaMenuSorting\d+/,
          /megaMenuHorizontalAlign\d+/
        ])
      )
    : {}
);

const enhancedWithMegaMenuItemsSelector = createShallowEqualSelector(
  [megaMenuSettingsSelector, menuItemsSelector, screenSelector],
  (settings, menuItems, screen): MenuItem[] =>
    menuItems.map((item) => ({
      ...item,
      megaOptions: getMegaOptions(item, settings, screen)
    }))
);

// 2. Choose between dots and regular menu
const onePageMenuItemsSelector = createShallowEqualSelector(
  [onePageTypeSelector, dotsMenuItemsSelector, enhancedWithMegaMenuItemsSelector],
  (onePageType, dotsMenuItems, megaMenuItems): MenuItem[] =>
    onePageType === "menu-one-page" ? dotsMenuItems : megaMenuItems
);

// 3. Filter selected menu items
const rootDisplaySelector = getSettingValueSelector(TargetSelector.Layout, "rootDisplay", "header.menu");
export const enhancedMenuItemsSelector = createShallowEqualSelector(
  [rootDisplaySelector, onePageMenuItemsSelector],
  (rootDisplay, onePageMenuItems) => {
    return filterMenu(onePageMenuItems, {
      currentOnly: rootDisplay === "only_children_items",
      cutChildren: false,
      pages: undefined,
      level: 100
    });
  }
);

export const menuOpenClickHoverSelector = getLayoutSettingValueSelector("trigger", "header.menu");

export const subMenuStyleSelector = combinedStyleSelector(
  TargetSelector.Layout,
  ["border", "boxShadow", "padding"],
  "header.submenu"
);

export const subMenuItemStyleSelector = combinedStyleSelector(
  TargetSelector.Layout,
  ["padding"],
  "header.submenu.item"
);

const subMenuItemHoverColorSelector = getLayoutColorSelector("hoverColor", "header.submenu.item.text");
export const subMenuItemHoverStyleSelector = createShallowEqualSelector(
  [subMenuItemHoverColorSelector],
  (color): React.CSSProperties => ({
    color
  })
);

export const menuShowSubTitlesSelector = getLayoutSettingValueSelector("subTitles", "header.menu");
